/*
  Mobile sticky add-to-cart bar.

  The bar owns no form state at all. It clones its contents from the real add-to-cart
  button (#ProductSubmitButton-<section>) and forwards clicks to it, so the submit runs
  through <product-form> with the variant, selling plan and quantity that the buy box
  currently holds. A MutationObserver keeps the cloned label and price in step when
  selling-plan-box.js or product-info.js rewrite the real button.

  On products with a subscription option the bar is the exception to that: it always
  adds a one-time purchase of the minimum quantity, while the buy box keeps its own
  default (usually the subscription). Label and price are swapped for the one-time
  ones, and the selling plan is dropped from the form data of that single submit.

  It shows while the real button is out of view and hides again as soon as the button
  or the footer is on screen, so it never parks on top of the footer.
*/
if (!customElements.get('product-sticky-atc')) {
  customElements.define(
    'product-sticky-atc',
    class ProductStickyAtc extends HTMLElement {
      constructor() {
        super();

        this.targetVisible = true;
        this.footerVisible = false;
        this.onClick = this.handleClick.bind(this);
        this.onBreakpointChange = this.update.bind(this);
      }

      connectedCallback() {
        this.target = document.getElementById(this.dataset.target);
        this.button = this.querySelector('.product-sticky-atc__button');

        if (!this.target || !this.button || !('IntersectionObserver' in window)) {
          this.remove();
          return;
        }

        this.mobile = window.matchMedia('(max-width: 749px)');
        this.mobile.addEventListener('change', this.onBreakpointChange);

        this.sync();
        this.contentObserver = new MutationObserver(this.sync.bind(this));
        this.contentObserver.observe(this.target, {
          attributes: true,
          childList: true,
          subtree: true,
          characterData: true,
        });

        // The bar covers the bottom strip of the viewport, so a button sitting behind
        // it does not count as visible.
        this.targetObserver = new IntersectionObserver(
          (entries) => {
            this.targetVisible = entries[entries.length - 1].isIntersecting;
            this.update();
          },
          { rootMargin: `0px 0px -${Math.round(this.offsetHeight) || 76}px 0px` }
        );
        this.targetObserver.observe(this.target);

        const footer = document.querySelector('.footer');
        if (footer) {
          this.footerObserver = new IntersectionObserver((entries) => {
            this.footerVisible = entries[entries.length - 1].isIntersecting;
            this.update();
          });
          this.footerObserver.observe(footer);
        }

        this.button.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        this.mobile?.removeEventListener('change', this.onBreakpointChange);
        this.contentObserver?.disconnect();
        this.targetObserver?.disconnect();
        this.footerObserver?.disconnect();
        this.button?.removeEventListener('click', this.onClick);
      }

      // The selling plan box is looked up on every use because product-info.js swaps
      // it out on variant changes. Only a box with a subscription card matters here.
      getSubscriptionBox() {
        const box = document.querySelector(`selling-plan-box[data-section="${this.dataset.section}"]`);
        return box?.querySelector('[data-radio-subscription]') ? box : null;
      }

      getOneTimeQuantity(box) {
        return Number.parseFloat(box.querySelector('[data-selling-plan-box-primary-quantity]')?.min) || 1;
      }

      sync() {
        this.button.innerHTML = this.target.innerHTML;

        const box = this.getSubscriptionBox();
        if (box && !this.target.disabled) {
          const label = this.button.querySelector('.buy-box__submit-label');
          const price = this.button.querySelector('[data-submit-price]');
          if (label && this.dataset.oneTimeLabel) label.textContent = this.dataset.oneTimeLabel;
          if (price && window.formatMoney) {
            price.textContent = window.formatMoney(
              Number(box.dataset.oneTimePrice) * this.getOneTimeQuantity(box),
              box.dataset.moneyFormat
            );
          }
        }

        this.button.disabled = this.target.disabled;
        this.button.classList.toggle('loading', this.target.classList.contains('loading'));

        if (this.target.hasAttribute('aria-disabled')) {
          this.button.setAttribute('aria-disabled', this.target.getAttribute('aria-disabled'));
        } else {
          this.button.removeAttribute('aria-disabled');
        }
      }

      // product-form.js builds its FormData synchronously inside the submit handler,
      // and new FormData(form) fires a "formdata" event first, so a listener around
      // the click can turn exactly this one submit into a one-time purchase without
      // touching the buy box. The listener is removed right after in case the submit
      // bailed out early (button busy) and never built the form data.
      handleClick(event) {
        event.preventDefault();

        const box = this.getSubscriptionBox();
        const form = this.target.form;
        if (!box || !form) {
          this.target.click();
          return;
        }

        const quantity = this.getOneTimeQuantity(box);
        const toOneTime = (formDataEvent) => {
          formDataEvent.formData.delete('selling_plan');
          formDataEvent.formData.set('quantity', quantity);
        };
        form.addEventListener('formdata', toOneTime, { once: true });
        this.target.click();
        form.removeEventListener('formdata', toOneTime);
      }

      update() {
        const show = this.mobile.matches && !this.targetVisible && !this.footerVisible;
        this.classList.toggle('product-sticky-atc--visible', show);
      }
    }
  );
}
